import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const FileBorrow = () => {
  const { fileID } = useParams();
  const [fileData, setFileData] = useState({
    url: null,
    type: null,
    name: "",
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let objectUrl = null;

    const fetchFile = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await axios.get(`/api/v1/file/asset/${fileID}`, {
          responseType: "blob",
        });

        const contentType = response.headers["content-type"];
        let fileName = "downloaded-file";

        // Extract filename from content-disposition header
        const contentDisposition = response.headers["content-disposition"];
        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/);
          if (fileNameMatch && fileNameMatch[1]) {
            fileName = decodeURIComponent(fileNameMatch[1]);
          } else {
            const fileNameMatchSimple = contentDisposition.match(/filename="([^"]+)"/);
            if (fileNameMatchSimple && fileNameMatchSimple[1]) {
              fileName = fileNameMatchSimple[1];
            }
          }
        }

        if (contentType && contentType.startsWith("image/")) {
          objectUrl = URL.createObjectURL(response.data);
          setFileData({ url: objectUrl, type: "image", name: fileName });
        } else if (contentType === "application/pdf") {
          objectUrl = URL.createObjectURL(response.data);
          setFileData({ url: objectUrl, type: "pdf", name: fileName });
        } else {
          // For other types, trigger download immediately
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", fileName);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);

          setFileData({ url: null, type: "download", name: fileName });
        }
      } catch (err) {
        console.error("Error fetching file:", err);
        setError(err.response?.data?.message || "Failed to fetch file. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchFile();

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [fileID]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading file...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="p-6 bg-white rounded-lg shadow-md max-w-md w-full text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-4">
      <div className="bg-white p-4 rounded-xl shadow-lg w-full max-w-5xl flex flex-col items-center">
        <div className="mb-4 w-full flex justify-between items-center px-4">
          <h1 className="text-lg font-semibold text-gray-700 truncate max-w-xs md:max-w-md">
            {fileData.name}
          </h1>
          <a
            href={fileData.url || "#"}
            download={fileData.name}
            className="text-sm text-blue-600 hover:underline font-medium"
          >
            Download File
          </a>
        </div>

        <div className="w-full h-[80vh] flex items-center justify-center overflow-hidden rounded-lg bg-gray-200">
          {fileData.type === "image" && (
            <img
              src={fileData.url}
              alt={fileData.name}
              className="max-w-full max-h-full object-contain"
            />
          )}
          {fileData.type === "pdf" && (
            <iframe
              src={fileData.url}
              className="w-full h-full"
              title={fileData.name}
            />
          )}
          {fileData.type === "download" && (
            <div className="text-center p-8">
              <div className="text-6xl mb-4">📄</div>
              <p className="text-gray-600">File has been downloaded automatically.</p>
              <p className="text-sm text-gray-400 mt-2">{fileData.name}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileBorrow;
